import { TextField, SelectMenu, Spinner } from '@scale/scaleui';
import {
  ConditionValue,
  ConditionValueType,
  OnboardingStepType,
} from '@scale/shared/onboardingControlPanel';
import {
  CountryCode,
  ResumeScreeningResult,
  ScreeningOutcome,
  WorkerSource,
} from '@scale/shared-types';
import {
  fetchTags,
  useSignupFlowInfo,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField as MuiTextField } from '@mui/material';
import React, { useState, useEffect } from 'react';
import apiFetch from 'react-scale/lib/api-fetch';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import { USStateCode } from '@scale/shared/types/demographics';

interface ConditionValueInputProps {
  id: string;
  value: ConditionValue;
  onChange: (value: ConditionValue) => void;
  disabled?: boolean;
  sourceStepConfigId?: string;
  sourceStepType?: OnboardingStepType;
}

const ConditionValueInput: React.FC<ConditionValueInputProps> = ({
  id,
  value,
  onChange,
  disabled,
  sourceStepConfigId,
  sourceStepType,
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [stringOptions, setStringOptions] = useState<string[]>([]);
  const { currentFlow } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
  }));
  const { isLoading: isLoadingLinks, data: signupFlowDetails } = useSignupFlowInfo(
    currentFlow?._id,
  );

  const handleFetchTags = async (search?: string) => {
    const tags = await fetchTags(search);
    setOptions(tags);
  };

  const handleFetchSourceStepOutcomes = async (
    sourceStepConfigId: string | undefined,
    sourceStepType: OnboardingStepType | undefined,
  ) => {
    if (!sourceStepConfigId || !sourceStepType) return;

    if (sourceStepType === OnboardingStepType.SkillScreening) {
      setStringOptions(Object.values(ScreeningOutcome));
    } else {
      const interviewOutcomes = await apiFetch(
        null,
        `corp-api/interview/interviewOutcomes/${sourceStepConfigId}`,
      );
      if (interviewOutcomes && interviewOutcomes.length > 0) {
        setStringOptions(interviewOutcomes);
      }
    }
  };

  useEffect(() => {
    handleFetchTags();
  }, []);

  useEffect(() => {
    handleFetchSourceStepOutcomes(sourceStepConfigId, sourceStepType);
  }, [sourceStepConfigId, sourceStepType]);

  return (
    <>
      {value.type === ConditionValueType.String &&
        (!stringOptions || stringOptions.length === 0) && (
          <TextField
            id={id}
            placeholder="Value to match"
            value={String(value.value)}
            onChange={e => onChange({ ...value, value: String(e.target.value) })}
            disabled={disabled}
          />
        )}
      {value.type === ConditionValueType.String && stringOptions && stringOptions.length > 0 && (
        <SelectMenu
          menuDirection="bottom"
          options={stringOptions.map(option => ({ id: option, name: option }))}
          selectedOptionId={value.value}
          setSelected={(v: string) => onChange({ ...value, value: v })}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.Number && (
        <TextField
          id={id}
          placeholder="Value to match"
          value={+value.value}
          type="number"
          onChange={e => onChange({ ...value, value: Number(e.target.value) })}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.Boolean && (
        <SelectMenu
          menuDirection="bottom"
          options={[
            {
              id: 'true',
              name: 'True',
            },
            {
              id: 'false',
              name: 'False',
            },
          ]}
          selectedOptionId={`${value.value}`}
          setSelected={(v: string) => onChange({ ...value, value: v === 'true' })}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.ResumeScreenResult && (
        <SelectMenu
          menuDirection="bottom"
          options={[
            {
              id: ResumeScreeningResult.Pass,
              name: ResumeScreeningResult.Pass,
            },
            {
              id: ResumeScreeningResult.Fail,
              name: ResumeScreeningResult.Fail,
            },
            {
              id: ResumeScreeningResult.HumanReview,
              name: ResumeScreeningResult.HumanReview,
            },
          ]}
          selectedOptionId={`${value.value}` as ResumeScreeningResult}
          setSelected={(v: ResumeScreeningResult) => onChange({ ...value, value: v })}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.CountryCodes && (
        <SelectMenu
          menuDirection="bottom"
          options={Object.values(CountryCode).map(code => ({ id: code, name: code }))}
          selectedOptionId={value.value}
          setSelected={(v: string[]) => onChange({ ...value, value: v as CountryCode[] })}
          multiple={true}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.USStateCodes && (
        <SelectMenu
          menuDirection="bottom"
          options={Object.values(USStateCode).map(code => ({ id: code, name: code }))}
          selectedOptionId={value.value}
          setSelected={(v: string[]) => onChange({ ...value, value: v as USStateCode[] })}
          multiple={true}
          disabled={disabled}
        />
      )}
      {value.type === ConditionValueType.SignupFlow && isLoadingLinks && <Spinner size={'sm'} />}
      {value.type === ConditionValueType.SignupFlow && !isLoadingLinks && signupFlowDetails && (
        <SelectMenu
          menuDirection="bottom"
          options={signupFlowDetails.map(detail => ({ id: detail.token, name: detail.name }))}
          selectedOptionId={value.value}
          setSelected={(v: string) => onChange({ ...value, value: v })}
          multiple={false}
          disabled={false}
        />
      )}
      {value.type === ConditionValueType.Tag && (
        <Autocomplete
          multiple
          disablePortal
          onChange={(_event, newValue) => {
            onChange({ ...value, value: newValue });
          }}
          value={value.value || []}
          id="tags-input"
          options={options}
          sx={{ width: 300 }}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onKeyDown={() => {
            handleFetchTags(inputValue);
          }}
          renderInput={params => <MuiTextField {...params} label="Tags" />}
        />
      )}
      {value.type === ConditionValueType.WorkerSource && (
        <SelectMenu
          menuDirection="bottom"
          options={Object.values(WorkerSource).map(source => ({ id: source, name: source }))}
          selectedOptionId={value.value}
          setSelected={(v: string) => onChange({ ...value, value: v })}
          multiple={false}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default ConditionValueInput;
